import type { FieldValue, Timestamp } from "firebase/firestore";
import type * as React from "react";
import type { Action } from "@reduxjs/toolkit";
import type { IntertieArchetypeIdType } from "./data/IntertieArchetypes";
import type { SavedCommitmentMetadata } from "./helpers/Commitment";

export type AudioLoadingType = "UNLOADED" | "LOADING" | "ERROR" | "LOADED";

export type MonthType =
  | "Jan"
  | "Feb"
  | "Mar"
  | "Apr"
  | "May"
  | "Jun"
  | "Jul"
  | "Aug"
  | "Sep"
  | "Oct"
  | "Nov"
  | "Dec";
export type DifficultyType = "Intern" | "Employee" | "Manager" | "VP" | "CEO";
export type SpeedType = "PAUSED" | "SLOW" | "NORMAL" | "FAST" | "ULTRA";

/** How an intertie may trade with neighbouring electricity markets. */
export type TradingPolicyType =
  "BALANCED" | "RELIABILITY_FIRST" | "SURPLUS_ONLY" | "CLOSED";

/** Authored facts about a neighbouring wholesale electricity market. */
export interface AdjacentMarketDefinitionType {
  id: string;
  name: string;
  description: string;
  archetype: IntertieArchetypeIdType;
  /** Set when the neighbour's seasons differ from the player's, e.g. across the equator */
  seasonHemisphere?: "NORTH" | "SOUTH";
  basePricePerMWh: number;
  availableSupplyW: number;
  availableDemandW: number;
  /** Carbon intensity varies by year; see importEmissionsKgco2ePerMWh */
  emissionsBasis: string;
  emissionsSource: string;
}

/** A buildable physical connection to one adjacent market. */
export interface TransmissionCorridorDefinitionType {
  id: string;
  adjacentMarketId: string;
  name: string;
  routeType: "EXISTING" | "NEW";
  capacityW: number;
  buildCost: number;
  annualOperatingCost: number;
  yearsToBuild: number;
  heatDerateStartsC: number;
  heatDeratePerC: number;
  solarDerateFraction: number;
}

export interface TransmissionLineOperatingType {
  paused?: boolean;
  id: number;
  corridorId: string;
  name: string;
  capacityW: number;
  buildCost: number;
  annualOperatingCost: number;
  yearsToBuildLeft: number;
  minuteCreated: number;
  financed: boolean;
  loanAmountLeft: number;
  loanMonthlyPayment: number;
  interestRate: number;
  // Inflation index when ordered; annual O&M escalates from it. Absent (1) at the run's start.
  costIndexAtBuild?: number;
  /**
   * Net power over this line in the current tick: positive importing, negative selling.
   * Derived display state, refreshed by live dispatch, month-boundary pre-rolls, and the
   * current-tick dispatch of a player-action reforecast. Future forecasts use cloned lines.
   * Set to 0 when ordered.
   */
  currentFlowW: number;
  /**
   * Embodied emissions from building the corridor, accrued over its construction period. Derived
   * from the corridor's cost per watt at purchase rather than stored per corridor, since the
   * authored data carries no route length.
   */
  constructionKgco2eTotal?: number;
  constructionKgco2eEmitted?: number;
  /**
   * Work in progress to widen a line that is already carrying power. The line keeps running at
   * its present rating throughout and steps up on the day the work finishes, which is how a
   * real reconductoring goes: crews restring one circuit at a time rather than taking the
   * interconnector down for a year. Absent unless an upgrade is under way.
   */
  upgrade?: IntertieUpgradeType;
}

export interface IntertieUpgradeType {
  targetCapacityW: number;
  buildCost: number;
  /** What the widened line will cost to run, replacing the current figure on completion. */
  annualOperatingCost: number;
  yearsToBuild: number;
  yearsToBuildLeft: number;
  constructionKgco2eTotal?: number;
  constructionKgco2eEmitted?: number;
}

export interface TransmissionStateType {
  tradingPolicy: TradingPolicyType;
  lines: TransmissionLineOperatingType[];
}

// Deliberately open rather than a union of the places that happen to ship today: a custom game
// may hold a location that isn't in LOCATIONS at all, so nothing is allowed to key off the
// closed set. Resolve one through getLocation / getScenarioLocation rather than indexing
// LOCATIONS directly, and check anything untrusted with isValidLocationId -- the id ends up in
// the path of a weather file.
export type LocationIdType = string;
export interface LocationType {
  id: LocationIdType;
  name: string;
  lat: number;
  long: number;
  // State / province / first-level subdivision from the city catalogue. Demand profiles use it
  // for location-specific structural trends such as Virginia Data Centers and Texas growth.
  admin?: string;
  // Curated cities carry an IANA zone. An arbitrary coordinate may not, in which case local time
  // is derived from longitude rather than from the player's computer.
  timeZone?: string;
  region?: string;
  country?: string;
  elevation?: number;
  // A curated upstream record used by conventional hydro. Locations without one use their own
  // precipitation, which keeps arbitrary custom locations playable without inventing a basin.
  watershedId?: LocationIdType;
  watershedName?: string;
  // Whether the loaded weather record includes a curated offshore wind site for this location.
  offshore?: boolean;
  // Explicit resource knowledge wins over the regional fallback. This keeps an arbitrary point
  // honest: coordinates alone cannot tell us whether a usable river or geothermal field exists.
  resources?: {
    geothermal?: boolean;
    hydro?: boolean;
  };
}

export type FuelNameType =
  | "Coal"
  | "Biomass"
  | "Wind"
  | "Offshore Wind"
  | "Airborne Wind"
  | "Sun"
  | "Natural Gas"
  | "Uranium"
  | "Oil"
  | "Geothermal"
  | "Hydro";
/** A simple-cycle peaking turbine, or a combined cycle that recovers its exhaust heat. */
export type GasCycleType = "simple" | "combined";
/** The fuels bought on a market. The rest are free once a plant is built. */
export type PricedFuelNameType =
  "Biomass" | "Coal" | "Natural Gas" | "Oil" | "Uranium";
/** $/Btu. Indexing by an unpriced fuel reads undefined. */
export type FuelPricesType = Record<PricedFuelNameType, number> &
  Partial<Record<FuelNameType, number>>;
/** Energy or power by fuel; the field it sits in names the unit. */
export type FuelProductionType = Partial<Record<FuelNameType, number>>;

/** The five high-level end-use groups used by the demand model and its stacked chart. */
export type DemandTypeNameType =
  | "Residential"
  | "Commercial"
  | "Industrial"
  | "Transportation"
  | "Data Centers"
  | "Mining";

export type DemandByTypeType = Record<DemandTypeNameType, number>;

export interface DifficultyMultipliersType {
  buildCost: number;
  expensesOM: number;
  buildTime: number;
  blackoutPenalty: number; // for each % of demand unfulfilled, how much the regional growth rate is reduced
  peakSharingImportLoss: number; // share of a peak-sharing neighbour's imports lost at full local heat/cold stress
  description: string; // shown in a tooltip on the difficulty picker
}

export type CardNameType =
  | "BUILD_GENERATORS"
  | "BUILD_STORAGE"
  | "BUILD_INTERTIES"
  | "FACILITIES"
  | "INSIGHTS"
  | "EVENTS"
  | "LOADING"
  | "MAIN_MENU"
  | "NEW_GAME"
  | "NEW_GAME_DETAILS"
  | "MANUAL"
  | "SETTINGS"
  | "SAVED_GAMES"
  | "CUSTOM_GAME"
  | "DATA_CENTER_SETUP"
  | "CHALLENGE";

export type ConceptNameType =
  | "money"
  | "supply"
  | "demand"
  | "blackout"
  | "customers"
  | "generator"
  | "storage"
  | "build"
  | "buy"
  | "reorder"
  | "pause"
  | "play"
  | "time"
  | "construction"
  | "finances"
  | "forecast"
  | "rate"
  | "fuel"
  | "weather"
  | "severeWeather"
  | "danger"
  | "goal";

export type StoryActionTargetType =
  | {
      card: "FACILITIES";
      view?: "FLEET" | "BUILD_GENERATORS";
      fuel?: FuelNameType;
    }
  | {
      card: "INSIGHTS";
      eventKey?: string;
      layer?: "FINANCES" | "SUPPLY_DEMAND" | "FUEL_PRICES";
    }
  | { card: "EVENTS" };

export type EvidenceTargetType =
  "supply-demand" | "finances" | "mission-details" | StoryActionTargetType;

export interface EvidenceRequestType {
  id: number;
  runId: number;
  target: EvidenceTargetType;
}

// What the card reducer's navigate action accepts, beyond a bare card name
export interface NavigateActionType {
  name: CardNameType;
  dontRemember?: boolean;
  // Most cards use the app's anonymous hash entry. Public routes can supply a meaningful URL,
  // while a popstate restoration must not write another browser-history entry.
  url?: string;
  skipBrowserHistory?: boolean;
  replaceCurrentCard?: boolean;
  // Manual entry to open and scroll to, for deep links from terms the game shows elsewhere
  entry?: string;
  storyTarget?: StoryActionTargetType;
}

export interface CardType {
  url?: string;
  name: CardNameType;
  ts: number;
  history?: CardNameType[];
  toPrevious?: boolean;
  entry?: string;
  storyTarget?: StoryActionTargetType;
}

// Every category a run can be scored on. Which ones apply depends on ownership; the rules
// themselves live in helpers/Scoring `SCORE_RULES`.
export type ScoreCategoryType =
  "supply" | "netWorth" | "customers" | "rate" | "emissions" | "blackouts";

// The per-category points that sum to `score`. Investor and public-ownership scenarios are
// scored on different categories (see helpers/Scoring), so the keys vary by scenario.
export type ScoreBreakdownType = Partial<Record<ScoreCategoryType, number>>;

export interface ScoreType {
  scenarioId: number;
  score: number;
  scoreBreakdown: ScoreBreakdownType;
  difficulty: DifficultyType;
  // A FieldValue on the way out (serverTimestamp() is resolved by Firestore, not by us) and a
  // Timestamp on the way back in
  date: Timestamp | FieldValue;
  uid: string;
  // The player's leaderboard name as it was when the score was set, denormalized so that
  // rendering a board is one query rather than one plus a profile read per row. Absent on scores
  // set before display names existed, and those fall back to Anonymous
  displayName?: string;
  // Id of the document in the `replays` collection holding the run that set this score, when one
  // was small enough to keep. A reference rather than the replay itself, so that opening a
  // leaderboard downloads fifty scores instead of fifty replays
  replayId?: string;
}

// The player actions a replay has to reproduce. Everything else about a run -- weather, fuel
// prices, demand -- falls out of the seed, so this is the whole of what the player contributed.
export type ReplayActionNameType =
  | "beginIntertieStress"
  | "chooseScenarioResponse"
  | "schedulePolicy"
  | "cancelPolicy"
  | "buildFacility"
  | "sellFacility"
  | "togglePauseFacility"
  | "reprioritizeFacility"
  | "buildTransmissionLine"
  | "upgradeTransmissionLine"
  | "cancelTransmissionLine"
  | "togglePauseTransmissionLine"
  | "setTradingPolicy"
  | "retrofitFacility"
  | "cancelRetrofit"
  | "delta";

export interface ReplayActionType {
  // Game minute the action was taken at, which is always a tick boundary
  minute: number;
  type: ReplayActionNameType;
  // The reducer's own payload, verbatim. Untyped here because it differs per action and comes
  // back off the network as untrusted JSON; decodeReplay is what makes it safe to apply
  payload: unknown;
}

export type MeaningfulDecisionKindType =
  "asset" | "sale" | "rate" | "policy" | "operation" | "dispatch" | "trading";

/** One accepted player decision after lifetime no-ops and reversals are coalesced. */
export interface MeaningfulDecisionType {
  key: string;
  lever: string;
  label: string;
  month: number;
  kind: MeaningfulDecisionKindType;
  before: string;
  after: string;
}

export interface ReplayType {
  appVersion: string; // For bug reports
  scenarioId: number;
  difficulty: DifficultyType;
  seed: number;
  // Where the run was played. A scenario id no longer pins this down -- a custom game carries its
  // own location, and an authored one could be given a location that isn't in LOCATIONS -- so
  // without it a replay would silently be re-simulated against a different city's weather
  location: LocationType;
  actions: ReplayActionType[];
  meaningfulDecisionGateWaived?: boolean;
}

/**
 * A replay on its way to or from Firestore. `actions` is a JSON string rather than a real array
 * so that the nested payloads can't run into Firestore's rules about what may sit inside one, and
 * so that the size measured against the 1 MiB document limit is the size actually stored.
 */
export interface ReplayDocType extends Omit<ReplayType, "actions"> {
  actions: string;
}

// Where a replay has got to while it's being watched
export interface ReplayPlaybackType {
  actions: ReplayActionType[];
  index: number; // Next action to apply
}

export interface LocalStoragePlayedType {
  scenarioId: number;
  date: string; // Stringified new Date()
  timesPlayed: number;
}

export interface DateType {
  minute: number;
  minuteOfDay: number; // 0 - 1439
  hourOfDay: number; // 0 - 23
  hourOfFullYear: number;
  percentOfMonth: number; // 0 - 1
  percentOfYear: number; // 0 - 1
  month: MonthType;
  monthNumber: number; // 1 - 12
  monthsElapsed: number;
  year: number;
}

export interface RawWeatherType {
  YEAR: number;
  MONTH: number;
  TEMP_C: number;
  CLOUD_PCT: number; // 0 - 100
  WIND_KPH: number;
  // Present only in v2 weather files whose catalogue entry has an offshore sampling point.
  WIND_OFFSHORE_KPH?: number;
  // Hydro turns this into watershed snowpack, runoff and reservoir inflow. Other downstream
  // effects such as snow sitting on panels and thermal-plant cooling water remain out of scope.
  PRECIP_MM: number; // in that hour
}

// All amounts are the average across the time window
export type TickPresentFutureType = Partial<FuelPricesType> &
  HistoryForecastShared & {
    minute: number;
    supplyW: number; // Watts
    availableSupplyW?: number; // Supply plus unused fuel-burning generation capacity
    demandW: number; // Watts
    reserveW?: number; // Signed supply margin plus local spare output reachable next tick
    importKgco2ePerMWh?: number; // Modeled mix of usable neighboring import capacity
    // Components sum to demandW; monthly chart averages preserve the breakdown in saves.
    demandByType: DemandByTypeType;
    solarIrradianceWM2: number;
    windKph: number;
    windOffshoreKph?: number;
    // Wind at the Airborne Wind curve's 100m reference height. Kept separate from windKph,
    // whose legacy onshore siting multiplier is already baked in.
    windAirborneKph: number;
    temperatureC: number;
    storedWh: number;
    precipitationMm: number; // Representative month's total over the hydro watershed
    snowpackMm: number; // Snow-water equivalent remaining in that watershed
    hydroRunoffMm: number; // Rain plus snowmelt available after catchment losses this month
    hydroReservoirWh: number; // Conventional-hydro reservoir energy, separate from storage
    hydroReservoirCapacityWh: number;
    hydroSpillWh: number; // Water above reservoir capacity lost during this tick
    hydroMandatedReleaseW: number; // Must-run water-rights flow through turbines
    storageChargeW?: number; // Actual grid draw before conversion losses
    storageDischargeW?: number; // Actual energy returned to the grid
    storageLossWh: number; // Charging conversion plus self-discharge / evaporation this tick
    // The exponentially smoothed bill customers respond to, rather than the slider's latest value
    customerRate: number;
    customerBillingRate?: number; // Delivered-energy blended rate, including enrolled offers.
    deferredResidentialWh?: number; // Unscaled representative-day energy awaiting late-evening use.
    deferredResidentialWhStart?: number; // Queue before this tick, retained when forecasts trim prior history.
    deferredResidential?: DeferredResidentialLoad[]; // Outstanding energy tied to its original recovery window.
    deferredResidentialStart?: DeferredResidentialLoad[]; // Before this tick, for repeated forecasts.
    shiftedResidentialW?: number; // Returned enrolled residential load, billed at the late rate.
    // Home and business load before rebate programs, and what efficiency and rooftop solar removed
    // from it this tick. Present only while a rebate program has installed anything.
    rebateEligibleW?: number;
    efficiencySavedW?: number;
    rooftopSolarW?: number;
    supplyByFuel: FuelProductionType;
    /** Positive gross flow into/out of the player's grid during this tick. */
    importedW?: number;
    exportedW?: number;
    transmissionCapacityW?: number;
    marketPricePerMWh?: number;
    renewableCapacityFactors?: Record<string, number>;
  };

export type DerivedHistoryKeysType = Exclude<
  NonNullable<
    {
      [Key in keyof DerivedHistoryType]: DerivedHistoryType[Key] extends number
        ? Key
        : never;
    }[keyof DerivedHistoryType]
  >,
  "minimumSupplyMarginW"
>;
export interface DerivedHistoryType extends MonthlyHistoryType {
  profit: number;
  profitPerkWh: number;
  revenuePerkWh: number;
  expenses: number;
  kgco2ePerMWh: number;
}

// Basically, downsample per-tick information so that I can store it for the entire game, which could go 100+ years
export interface MonthlyHistoryType extends HistoryForecastShared {
  // Compact monthly averages for all non-financial Insights layers.
  chartAverage?: TickPresentFutureType;
  chartTickWeight?: number;
  year: number;
  month: number;
  supplyWh: number; // total
  demandWh: number; // total
  // Persisted simulation facts used by story checkpoints. These are deliberately raw totals,
  // not narrative classifications such as "reliable" or "gas-heavy".
  deliveredWhByFuel: FuelProductionType;
  peakDemandW: number;
  // The tightest instantaneous reserve in the month. Negative means supply fell short. Kept so
  // scenario debriefs can explain an event without retaining every tick for the whole run.
  minimumSupplyMarginW?: number;
}

interface HistoryForecastShared {
  expensesPolicy: number; // Monthly funded upgrades.
  revenueExports: number;
  revenueGrants: number; // Scenario grants, excluded from customer electricity rates
  expensesImports: number;
  cash: number;
  customers: number;
  netWorth: number;
  revenue: number; // total
  expensesFuel: number; // total
  expensesOM: number; // total
  expensesCarbonFee: number; // total
  expensesInterest: number; // total - only the interest payments count as an expense, the rest is just a settling of balances between cash and liability
  kgco2e: number; // Local generation, purchased electricity and construction under way
  localKgco2e?: number;
  importedKgco2e?: number;
  // Embodied emissions from everything currently being built, spread evenly across each
  // project's construction period. Deliberately outside the carbon fee's base: a fee prices
  // what a grid burns, while this is mostly incurred in someone else's supply chain.
  constructionKgco2e?: number;
  // Point in time rather than totals: what a new loan would cost, and what prices were doing,
  // as of this tick / the end of this month. Summing them would be meaningless, so reduceHistories
  // keeps the last one it sees, the way it does for cash and net worth.
  interestRate: number; // Annual, as a fraction. Prime plus the company's own credit premium
  inflationRate: number; // Annualised, as a fraction. Can be negative
}

export interface FuelType {
  // costPerBtu: number; // Measured from raw stock / before generator efficiency loss
  // all costs should be in that year's $ / not account for inflation when possible
  kgCO2ePerBtu: number; // Measured from raw stock / before generator efficiency loss
  // Counted towards emissions and the score, but not charged the carbon fee (biogenic CO2)
  feeExempt?: boolean;
}

/**
 * `A` with every field that only `B` declares typed as absent. A union of two such types can be
 * narrowed on any one-sided field (`if (facility.peakWh)`), and reading a one-sided field before
 * narrowing yields `T | undefined` instead of a compile error.
 */
type Exclusive<A, B> = A & { [K in Exclude<keyof B, keyof A>]?: undefined };

/** Storage is the member with `peakWh`; narrow with `isStorage` or on that field directly. */
export type FacilityOperatingType =
  | Exclusive<GeneratorOperatingType, StorageOperatingType>
  | Exclusive<StorageOperatingType, GeneratorOperatingType>;

/** Narrows a shopping or operating facility union to its storage member. */
export function isStorage<T extends { peakWh?: number }>(
  facility: T,
): facility is Extract<T, { peakWh: number }> {
  return facility.peakWh !== undefined;
}

interface SharedOperatingType
  extends LoanInfo, LifetimeTotals, ConstructionEmissions {
  id: number; // Monotonically increasing
  // Signed for storage: negative while charging
  currentW: number;
  yearsToBuildLeft: number;
  minuteCreated: number; // That the user clicked buy, not construction complete
  // Set when construction completes; absent while the facility is still being built.
  minuteOperational?: number;
  // Absent until the player first toggles it
  paused?: boolean;
  // The game's inflation index on the day it was bought, which its quoted O&M, variable O&M and
  // start costs are denominated in. Operating costs escalate from here with the index rather
  // than staying frozen in purchase-year dollars. Absent (1) for the starting fleet.
  costIndexAtBuild?: number;
}

export interface GeneratorOperatingType
  extends GeneratorShoppingType, SharedOperatingType {
  // A retrofit being installed, which holds the plant offline until it completes.
  upgradeInProgress?: FacilityUpgradeInProgressType;
  // Unit commitment is distinct from instantaneous output: a plant ramps through outputs below
  // its stable minimum while starting and stopping, but cannot remain there indefinitely.
  committed?: boolean;
  // Last real-tick state for start edge detection. Forecast/pre-roll dispatch mutates currentW,
  // so currentW alone cannot distinguish a player-visible start from a synthetic one. For
  // minimum-load plants this records commitment rather than a literal positive currentW.
  generatingLastRealTick?: boolean;
  reservoirWh?: number;
  hydroLastInflowWh?: number;
  hydroLastSpillWh?: number;
  hydroLastMandatedReleaseWh?: number;
  hydroLastBypassWh?: number;
}

export interface StorageOperatingType
  extends StorageShoppingType, SharedOperatingType {
  currentWh: number;
}

/**
 * What building this asset emits in total, resolved from the shopping quote at purchase so a
 * later price or technology revision cannot retroactively change what a standing plant emitted.
 * Accrued into the company's totals over the construction period rather than booked at once.
 * Absent on the starting fleet, which was built before the run began.
 */
export interface ConstructionEmissions {
  constructionKgco2eTotal?: number;
  /**
   * How much of that total the run has already booked. Kept per asset so accrual is driven by
   * how far the build has actually got rather than by elapsed time: the month-boundary pre-roll
   * advances a facility's clock on frames that are never recorded, so a tick-shaped share would
   * quietly lose whatever the pre-roll moved. Catching up against progress cannot.
   */
  constructionKgco2eEmitted?: number;
}

/**
 * What one facility has actually done since it came online, so a row can report whether it is
 * earning its keep rather than only what it cost to build. Accumulated per tick by
 * updateSupplyFacilitiesFinances, and only while the game is really ticking -- a forecast runs
 * against a deep clone of the fleet and throws the clone away, so its ticks never land here.
 */
export interface LifetimeTotals {
  lifetimeWh: number; // Delivered to the grid. Storage counts discharge only, not charging
  // What it could have delivered running flat out over the same span, ie the denominator of its
  // capacity factor. Accrues from the moment construction finishes, so pauses and idle hours
  // count against it the way they do for a real plant
  lifetimePotentialWh: number;
  lifetimeRevenue: number; // Its pro-rata share of what the company sold
  lifetimeExpenses: number; // Its own fuel, O&M, carbon fees and loan interest
  // Representative starts: one on/off edge in the sampled day stands for every day in its month.
  // Inherited plants include estimated starts from their age and typical technology duty.
  // Present only for generators whose maintenance model tracks starts.
  lifetimeStarts?: number;
}

interface LoanInfo {
  loanAmountTotal: number;
  loanAmountLeft: number;
  loanMonthlyPayment: number;
  // The rate this particular loan was signed at, fixed for its whole 30 year life. Kept per
  // facility rather than read from the current rate so that a later spike cannot push a month's
  // interest above a payment that was struck years earlier, which would grow the balance forever.
  // 0 for anything bought outright.
  interestRate: number;
}

/** Storage is the member with `peakWh`; narrow with `isStorage` or on that field directly. */
export type FacilityShoppingType =
  | Exclusive<GeneratorShoppingType, StorageShoppingType>
  | Exclusive<StorageShoppingType, GeneratorShoppingType>;

export interface StorageShoppingType extends SharedShoppingType {
  peakWh: number;
  maxPeakWh: number; // Maximum size the technology is currently buildable
  roundTripEfficiency: number; // 0 - 1, percentage (even though it's round trip, applied when inserting so capacity looks correct-to-user)
  hourlyLoss: number; // 0 - 1, percentage (water evaporation, heat loss, etc)
  constructionKgco2ePerWh?: number; // See constructionKgco2ePerW
}

export interface GeneratorShoppingType extends SharedShoppingType {
  fuel: FuelNameType;
  maxPeakW: number; // Maximum size the technology is currently buildable
  capacityFactor: number; // 0 - 1, percent of theoretical output actually produced across a year
  // Fraction of nameplate output permanently lost each operating year. Optional because most
  // generator types do not have a well-supported secular output decline.
  annualOutputDegradation?: number;
  btuPerWh: number; // Heat Rate, but per W for less math per frame
  // Lowest steady output as a fraction of nameplate. Starting and shutdown ramps may pass below
  // it transiently; an online unit otherwise produces at least this much.
  minimumStableOutput?: number;
  // "Natural Gas" only: the turbine arrangement. Both burn the same fuel, so fuel-keyed rules
  // (prices, cold snaps, charts) treat them alike; this names the technology without relying on
  // the display name, so a later simple-to-combined conversion can change it in place.
  gasCycle?: GasCycleType;
  // Explicit because neither purchased fuel nor a start charge identifies every thermal plant:
  // geothermal buys no fuel, while the Oil facility is an internal-combustion generator.
  tracksStarts?: boolean;
  // Non-fuel expense charged for one physical start. Only present when the technology's source
  // case reports a transferable amount separately from fixed and output-dependent O&M.
  costPerStart?: number;
  // How many starts a year the build quote assumes when annualizing costPerStart. Absent means
  // one a day (ASSUMED_STARTS_PER_YEAR), the peaking turbine's duty; live play charges actual
  // starts either way.
  assumedStartsPerYear?: number;
  // Non-fuel O&M charged against actual generation. Technologies without a separately sourced
  // variable component annualize all non-fuel operating expense into annualOperatingCost.
  variableOperatingCostPerMWh?: number;
  // Conventional hydro only. whPerMm is calibrated against the loaded watershed record so that
  // long-run inflow lands on capacityFactor without flattening wet and dry years.
  reservoirCapacityWh?: number;
  hydroWhPerMm?: number;
  hydroMeanMonthlyInflowWh?: number;
  // Weather hardening chosen at purchase or added later by retrofit. See src/helpers/Hazards.tsx.
  resilience?: FacilityResilienceType;
  // The part of buildCost that pays for the resilience option, kept on the quote so the build
  // dialog can add and remove the option exactly. Dropped once the quote becomes a facility.
  resilienceExtraBuildCost?: number;
  // Levelized cost per Wh at quote time. Infinity when fuel prices have not loaded yet.
  lcWh: number;
  /**
   * Embodied emissions from building the thing: materials, manufacturing, transport and
   * installation, excluding everything the plant does once it runs. Generators carry a per-watt
   * figure and storage a per-watt-hour one; neither carries both, because each storage
   * technology has a fixed duration, which would make a second coefficient unidentifiable.
   * Resolved against the catalogue's year at purchase, so a quote locks its vintage.
   */
  constructionKgco2ePerW?: number;
}

interface SharedShoppingType {
  hydroSiteId?: string;
  name: string;
  description: string;
  available: boolean;
  buildCost: number; // Partially fixed, partially variable (such as size dependent)
  // When more information is not available, assume that average costs = 1/4 fixed (for avg size), 3/4 variable
  // all costs should be in that year's $ / not account for inflation when possible
  annualOperatingCost: number;
  // all costs should be in that year's $ / not account for inflation when possible
  peakW: number;
  // Present only for technologies whose geography imposes a finite number of project sites.
  // Includes projects under construction because choosing to build has already claimed the site.
  viableLocationsRemaining?: number;
  lifespanYears: number;
  yearsToBuild: number;
  spinMinutes: number; // 1 for renewables, avoiding repeated fallback coercion in the tick loop
}

export interface TutorialStepType {
  skipBeacon?: boolean;
  // The card this step's target lives on. Every step change navigates here, in both
  // directions, so stepping backwards over a step that navigated forwards still lands on
  // the card holding the target instead of leaving the objective pointing at absent UI
  card?: CardNameType | NavigateActionType;
  // A one-way side effect of leaving this step forwards, such as starting the clock. It
  // isn't replayed when stepping backwards, since nothing would undo it - navigation
  // belongs in `card`, which works in both directions
  onNext?: () => Action;
  // Name an explicit transition when continuing starts a fresh challenge.
  nextLabel?: string;
  // Optional for unguided capstones: ordinary objectives can point at a control for a restrained
  // outline, while a capstone deliberately leaves the player to find the answer themselves.
  target?: string;
  // The one thing to do right now, as a short imperative ("Tap 1× to start time"). Every step
  // has one, explanations included - there it names what to look at and says to tap Next - so
  // the objective always opens on a deed instead of a paragraph to read
  action: string;
  content: React.JSX.Element;
  // Game chrome this step doesn't need yet. The first mission starts almost bare and reveals
  // controls as they're taught, so a new player isn't choosing between a dozen buttons before
  // they've made their first move. Presentation only: the gates never depend on it
  hideUi?: TutorialUiIdType[];
  // Extra help, always shown beneath the step's sentence. Kept outside content so authored
  // steps state the task first and the help second, in a consistent style.
  hint?: React.ReactNode;
  // Present = the step is action-gated ("play, don't tell"): the HUD shows a "complete
  // objective" status instead of a Next button, and the walkthrough advances the moment this
  // returns true. Evaluated after every dispatch - including every tick - so keep it to
  // cheap field reads. Tutorial scenarios have fixed authored starting states, so
  // predicates are absolute (e.g. facilities.length >= 2), never relative to step entry
  advanceOn?: (state: AppStateType) => boolean;
  // Explanations still offer Next, but completed deeds can move them along too.
  continueOn?: (state: AppStateType) => boolean;
  // Presentation-only controls. Game mutations use predicates to exclude rejected actions.
  continueOnClick?: string;
  // Gate for deeds that leave no distinguishable state behind (a drag re-order, a pause
  // toggle): advance when an action with one of these types is dispatched. Either gate
  // field alone makes the step gated; both may be combined (OR)
  advanceOnAction?: string | string[];
  // An independent application check. By default, entering one restarts the authored scenario at
  // this step, giving it a deterministic, retryable state instead of inheriting whatever the
  // guided portion changed. A capstone that explicitly builds on the guided work can preserve it.
  // Success advances normally; failure pauses for consequence-specific feedback.
  capstone?: {
    preserveProgress?: boolean;
    // Optional authored checkpoint overrides. The normal tutorial and its capstone can therefore
    // teach with different starting economics while still rebuilding the whole Game slice through
    // initGame on entry/retry. Anything omitted inherits the mission's scenario-level value.
    checkpoint?: {
      cash?: number;
      dollarsPerkWh?: number;
      facilities?: ScenarioFacilityType[];
      startingCustomers?: number;
    };
    success: (state: AppStateType) => boolean;
    failure?: (state: AppStateType) => boolean;
    successMessage: string;
    failureMessage: string;
  };
  // Above the desktop breakpoint the bottom nav is hidden and Facilities / Insights render
  // side by side, so a step whose target lives in that nav - or whose
  // selector matches more than one pane - needs a different target there, and usually
  // different wording too, since there are no tabs left to switch between
  desktop?: {
    target: string;
    action?: string;
    content?: React.JSX.Element;
  };
}

// Chrome a tutorial step can hide. Most ids map to selectors in app.scss under
// [data-tutorial-hide]; sidePanes is dropped in Compositor.renderCard instead, since the pane grid
// has resizable tracks that hiding a pane with CSS would leave empty. TUTORIAL_UI_SELECTORS lists
// what each one hides for the authoring test
export type TutorialUiIdType =
  | "nav" // bottom navigation, and its hotkeys
  | "build" // Build button, and its hotkeys
  | "menu" // the ⋮ game menu
  | "speed" // speed buttons, and their hotkeys
  | "facilityActions" // row actions (Pause, Sell, Move) and drag handles
  | "yearProgress" // the thin year progress bar
  | "sidePanes"; // Insights and Events beside Facilities on tablets and desktops

export const TUTORIAL_UI_SELECTORS: Record<TutorialUiIdType, string[]> = {
  nav: ["#navfooter", "#insightsNav", "#eventsNav", "#faciltiesNav"],
  build: [".button-buildFacility"],
  menu: [".gameMenuButton"],
  speed: ["#speedChangeButtons"],
  facilityActions: [".facilityActions", ".facilityDragHandle"],
  yearProgress: ["#yearProgressBar"],
  sidePanes: ["#insightsPane", "#eventsPane"],
};

export function isGatedStep(step: TutorialStepType): boolean {
  return !!(step.advanceOn || step.advanceOnAction || step.capstone);
}

// A walkthrough moving between two steps. Both ends are named because Back and Next need
// telling apart: a step's onNext only applies to leaving it forwards
export interface TutorialStepChangeType {
  fromStep: number;
  toStep: number;
  tutorialSteps: TutorialStepType[] | undefined;
  scenarioId: number;
  currentCard: CardNameType;
}

export type ScenarioBriefingToneType =
  "transition" | "boom" | "island" | "innovation" | "storm" | "legacy";

export type ScenarioThemeType =
  "Extreme weather" | "Energy transition" | "Rapid growth";

/**
 * The authored promise of a scenario, kept beside its simulation setup so the mission list and
 * briefing screen tell the same story. Each scenario's dedicated icon supplies the visual tone.
 */
export interface ScenarioBriefingType {
  tone: ScenarioBriefingToneType;
  fantasy: string;
  objective: string;
  threat: string;
}

export interface ScenarioType {
  /** Opt-in official event arcs for a custom run; location and starting fleet stay custom. */
  eventScenarioIds?: number[];
  hydroInventoryKey?: string;
  // Persisted in scores, completion and shared links; append new ids rather than renumbering
  id: number;
  name: string;
  icon: string; // assumed to be images/<string>.svg
  locationId: LocationIdType;
  // The full location, for scenarios whose location isn't in LOCATIONS -- which is every custom
  // game, since the player may eventually pick a place no table knows about. When it's set it
  // wins over locationId; getScenarioLocation is the one place that resolves the two.
  location?: LocationType;
  summary?: string;
  briefing?: ScenarioBriefingType;
  // Player-facing browse categories. A challenge may appear in more than one theme.
  themes?: ScenarioThemeType[];
  // Lower numbers appear first in the compact recommendations; omitted challenges follow.
  recommendationOrder?: number;
  ownership: "Investor" | "Public";
  tutorialSteps?: TutorialStepType[];
  /** Explicit scenario override. Tutorials default off; ordinary games use location availability. */
  intertiesEnabled?: boolean;
  // Pins the run's RNG so it plays out identically every time. Every authored scenario leaves
  // this off and draws a fresh seed each play; only the custom game screen sets it
  seed?: number;
  startingYear: number;
  cash: number;
  // When absent, the location profile supplies the starting grid size.
  startingCustomers?: number;
  // Customer count and utility-scale load are not interchangeable. Authored scenarios can
  // calibrate the ordinary customer-driven baseline; otherwise the location profile is enough.
  startingDemandScale?: number;
  /** Optional weather/daily peak calibration; 1 preserves the original demand shape. */
  demandShapeExponent?: number;
  // Absolute loads owned by this scenario. A Data Centers schedule replaces the generic regional
  // data-center curve unless marked as a marginal addition with supplementsBackground.
  loadAdditions?: ScenarioLoadAdditionType[];
  /** Optional mission gate evaluated at the authored end date. */
  minimumCustomerRetention?: number;
  /** Optional requirement to serve a specific month without falling below a reliability target. */
  reliabilityObjective?: {
    year: number;
    month: number;
    /** Consecutive calendar months that must each meet the target. Defaults to one. */
    durationMonths?: number;
    minimumDemandServed: number;
    label: string;
  };
  dollarsPerkWh: number;
  durationMonths: number;
  endTitle?: string;
  endMessage?: string;
  feePerKgCO2e: number;
  facilities: ScenarioFacilityType[];
}

/** An authored starting asset may already have spent years in service when a scenario opens. */
export type ScenarioFacilityType = Partial<FacilityShoppingType> & {
  initialAgeYears?: number;
  /**
   * Share of its reservoir a starting hydro plant holds on the opening tick. Defaults to the
   * neutral half-pool. A scenario that opens on a known lake level, or in a hemisphere whose
   * dry season arrives before its first wet one, has to be able to say so.
   */
  initialReservoirFraction?: number;
  /**
   * Optional player-facing name for an authored aggregate or gameplay proxy. It replaces the
   * facility's name, which the fleet also uses to find its icon, so a label on anything but a
   * Uranium plant needs matching artwork under `public/images/`.
   */
  label?: string;
};

export interface ScenarioLoadAdditionType {
  id: string;
  label: string;
  startsYear: number;
  /** Calendar month, 1-12. Defaults to January. */
  startsMonth?: number;
  /** Maximum total load after the start date, never an annual increment. */
  peakW: number;
  loadFactor: number;
  /** Adds a marginal campus on top of existing regional data-center demand and growth. */
  supplementsBackground?: boolean;
  /**
   * The two end uses a scenario can own outright. Both sit outside the regional sector mix: a
   * grid either has the mine or the campus on it or it does not, and neither scales with the
   * customer count. "Data Centers" replaces the generic regional curve unless supplementsBackground is true; "Mining"
   * has no generic curve and is purely additive.
   */
  demandType: "Data Centers" | "Mining";
}

/**
 * What kind of thing happened, which is all the event log's icons and colours are keyed off.
 * The text itself is written where the event is raised, since that's the only place that knows
 * how much energy went unserved or which fuel moved.
 */
export type GameEventKindType =
  | "BLACKOUT"
  | "BLACKOUT_OVER"
  | "CONSTRUCTION"
  | "BUILD"
  | "SELL"
  | "LOAN"
  | "FUEL_PRICE"
  | "FUEL_CROSSOVER"
  | "WORLD_EVENT";

export type GameEventImportanceType = "ROUTINE" | "NOTABLE" | "CRITICAL";

/**
 * One line of the company's history.
 *
 * A blackout used to be a toolbar that pulsed and then stopped, a finished plant a toast that
 * lasted four seconds - so a player who was looking at another pane, or away from the screen,
 * had no way to find out what had happened to them. These are kept instead.
 */
export interface GameEventType {
  // Monotonic within a run, so React has a key that doesn't move when the log is trimmed
  id: number;
  kind: GameEventKindType;
  // When it happened, as the game clock read it at the time ("Mar 2024")
  label: string;
  message: string;
  // Most entries are passive history. Important entries can interrupt the clock, stand out in
  // the log and take the player to the screen where the consequence can be investigated.
  importance?: GameEventImportanceType;
  actionTarget?: StoryActionTargetType;
  title?: string;
  concept?: ConceptNameType;
  storyPhaseKey?: string;
  turningPointPriority?: number;
}

export type StoryAttributeValueType =
  string | number | boolean | string[] | number[];

/**
 * Authored regional wildfire hazard for recurring, location-aware emergencies.
 *
 * These are explicitly simplified game balance values, not observed fire statistics: the monthly
 * weights shape a seasonal risk that is never a guaranteed fire, and the severity bounds are the
 * readable range an incident may land in. Unknown locations carry no profile and therefore no
 * inferred risk.
 */
export interface WildfireProfileType {
  /** Expected disruptive incidents per year at baseline weather (sum of monthly weights is one). */
  annualHazard: number;
  /** Twelve monthly weights, index 0 = January, summing to one over the year. */
  monthlyWeights: number[];
  /** Share of customer load disconnected by safety shutoffs, sampled per incident. */
  disconnectedDemand: { min: number; max: number };
  /** Output retained by affected generators, sampled per incident. */
  outputMultiplier: { min: number; max: number };
  /** Share of fleet peak capacity constrained, sampled per incident. */
  targetCapacityShare: { min: number; max: number };
  /** Restoration cost per MWh of exposed monthly demand, charged each active month. */
  restorationCostPerMWh: number;
  /** Months after an incident before the next one may start. */
  cooldownMonths: number;
  /** Month (0-based) each fire season starts, used for the preparedness preview. */
  preparednessMonth: number;
  /** How many months the fire season lasts; ongoing preparedness also covers off-season fires. */
  preparednessDurationMonths: number;
}

export type WeatherHazardType = "HAIL" | "EXTREME_COLD";
export type ResilienceUpgradeType =
  "hailResistant" | "coldWeatherPackage" | "solarTrackers";

/** Weather hardening on one facility. Each field only applies to the fuel noted beside it. */
export interface FacilityResilienceType {
  hailResistant?: boolean; // "Sun" only
  // "Sun" only, and only at build: single-axis trackers that follow the sun and stow in hail
  solarTrackers?: boolean;
  // "Sun" with trackers: the share of hail damage a tracked array still takes, fixed at build by
  // the stow angle the trackers of that year could reach.
  trackerHailDamageFactor?: number; // (0, 1]
  coldWeatherPackage?: boolean; // "Natural Gas" fuel only, either cycle
  // "Natural Gas" fuel only: the coldest representative-day minimum the plant runs through without a
  // derate, resolved at build or retrofit from the location so it cannot drift afterwards.
  designMinTempC?: number; // [-60, 0]
}

/**
 * Location exposure to hail and extreme cold. Simplified game balance rounded from published
 * climatologies, not a site-specific risk assessment; see src/data/Hazards.tsx for sources.
 */
export interface WeatherHazardProfileType {
  /** Expected hailstorms per year damaging enough to break utility PV modules at a site. */
  damagingHailPerYear: number;
  /** Whether plants are winterized by default here, and cold must be deeper to strain gas supply. */
  coldClimate: boolean;
  /** Representative-day minimum below which regional gas supply is strained; defaults by climate. */
  regionalColdThresholdC?: number;
  source: string;
}

/** The replayed payload for adding a resilience upgrade to a standing facility. */
export interface RetrofitFacilityAction {
  facilityId: number;
  upgrade: FacilityUpgradeType;
}

export type FacilityUpgradeType = ResilienceUpgradeType | "combinedCycle";

/**
 * A retrofit being installed on a standing facility. The plant is offline from startsMinute until
 * completesMinute, when the upgrade takes effect; cancelling before then refunds `cost` in full.
 */
export interface FacilityUpgradeInProgressType {
  upgrade: FacilityUpgradeType;
  cost: number; // What the player paid, and what a cancellation refunds
  startsMinute: number;
  completesMinute: number;
}

export interface WorldEventEffectsType {
  fuelPriceMultipliers?: Partial<Record<FuelNameType, number>>;
  temperatureOffsetC?: number;
  demandMultiplier?: number;
  /** Multiplies modeled watershed inflow before it reaches a hydro reservoir. */
  hydroRunoffMultiplier?: number;
  // An override rather than a multiplier. Content validation rejects overlapping policy phases.
  carbonFeePerKgCO2e?: number;
  buildCostMultipliersByFuel?: Partial<Record<FuelNameType, number>>;
  operatingCostMultipliersByFuel?: Partial<Record<FuelNameType, number>>;
  /** Fixed company-wide operating expense charged once per active story month. */
  operatingExpensePerMonth?: number;
  facilityOutputMultipliersByFuel?: Partial<Record<FuelNameType, number>>;
  facilityOutputMultipliersById?: Record<string, number>;
}

/** Pure simulation summary exposed to authored story phases. */
export interface StorySnapshotType {
  deliveredWhByFuel12m: Partial<Record<FuelNameType, number>>;
  demandWh12m: number;
  unservedWh12m: number;
  netIncome12m: number;
  peakDemandW12m: number;
  firmPeakW: number;
  storagePeakW: number;
  storagePeakWh: number;
  facilities: Array<{
    id: number;
    name: string;
    fuel?: FuelNameType;
    ageYears: number;
    peakW: number;
    operational: boolean;
  }>;
}

/** A pure summary of an event's completed monthly window. */
export interface StoryPeriodSnapshotType {
  deliveredWhByFuel: Partial<Record<FuelNameType, number>>;
  demandWh: number;
  unservedWh: number;
  netIncome: number;
  peakDemandW: number;
}

/** One deterministic, time-bounded occurrence created by the world-event engine. */
export interface ActiveWorldEventType {
  // Definition id plus location/date, stable across a replay of the same run
  key: string;
  definitionId: string;
  startsMinute: number;
  endsMinute: number;
  attributes: Record<string, StoryAttributeValueType>;
  effects: WorldEventEffectsType;
  /** False for surprises that must not appear in the event list or forward forecasts. */
  forecastable?: boolean;
  // Presentation travels with a persisted occurrence so the Events pane can continue to explain
  // an ongoing effect after a save/load without re-resolving authored content.
  title?: string;
  message?: string;
  concept?: ConceptNameType;
  importance?: GameEventImportanceType;
  actionTarget?: StoryActionTargetType;
}

export interface WorldEventStateType {
  active: ActiveWorldEventType[];
  // Resolved live occurrences survive expiry so recovery copy, saves, replays, matrix reports,
  // and debrief selection can refer to the exact onset-time attributes and facility IDs.
  occurrences: ActiveWorldEventType[];
  // A bounded record of month/definition checks prevents a save resumed in the same month from
  // drawing (and announcing) the same occurrence twice.
  checkedKeys: string[];
}

export type PolicyId = "efficiency" | "solar" | "timeOfUse" | "curtailment";
export type PolicyTier = "Off" | "On";
export interface DeferredResidentialLoad {
  energyWh: number; // Unscaled representative-day energy.
  recoveryStartMinute: number; // Absolute simulation minute, including across month boundaries.
  recoveryEndMinute: number;
}
export interface PolicyProgramType {
  tier: PolicyTier;
  startHour?: number; // Four-hour local-clock window; absent means 17 for earlier callers.
  adoption: number; // Build-out progress (0-1) for rebates; current enrolled share for operating offers.
  spending: number; // Funded upgrades this month; offer credits instead reduce billed revenue.
  spent: number; // Cumulative rebate spending over the run.
  completedMonth?: number; // Last month a finished build-out installed upgrades.
  installs?: [month: number, share: number][]; // Build-out cohorts, for measures that wear out.
  pending?: { tier: PolicyTier; month: number; startHour?: number };
}
export interface PoliciesType {
  month: number; // Last processed elapsed month; prevents duplicate enrollment and charges.
  programs: Record<PolicyId, PolicyProgramType>;
}
export interface PolicyChangeType {
  id: PolicyId;
  tier: PolicyTier;
  month: number;
  startHour?: number;
}
export interface GameType {
  runIdentity?: RunIdentity;
  challenge?: ChallengeInvitation;
  policies?: PoliciesType;
  policyPause?: { token: string; speed: SpeedType };
  /** Presentation speed restored after the pending scenario decision. */
  scenarioChoicePause?: SpeedType;
  seed: number;
  difficulty: DifficultyType;
  scenarioId: number;
  // The scenario the player assembled on the custom game screen. Authored scenarios live in
  // SCENARIOS, this one only ever exists here, so every lookup has to go through getScenario()
  customScenario?: ScenarioType;
  location: LocationType;
  speed: SpeedType;
  inGame: boolean;
  feePerKgCO2e: number;
  dollarsPerkWh: number;
  // Customer price competition starts from the scenario's rate and half of this addressable pool.
  // customerRate is the three-month bill average carried between monthly forecast windows.
  customerMarketSize: number;
  customerRate: number;
  // Copied from the scenario at init so saves carry the exact demand contract they started with.
  startingDemandScale: number;
  demandShapeExponent?: number;
  loadAdditions: ScenarioLoadAdditionType[];
  // What a loan signed right now would cost, and the multiplier on prime that gets there.
  // Both recomputed once a month. The premium is kept separately so that a forecast can price
  // future months against where prime is heading while holding the company's own creditworthiness
  // fixed -- what the player does next is exactly what the forecast cannot know.
  interestRate: number;
  creditPremium: number;
  tutorialStep: number;
  tutorialIntertieStress?: {
    active: boolean;
    startsMinute: number;
    suppliedTicks: number;
    completed: boolean;
  };
  date: DateType;
  startingYear: number;
  timeline: TickPresentFutureType[]; // anything before currentMinute is history, anything after is a forecast
  monthlyHistory: MonthlyHistoryType[]; // live updated; for calculation simplicity, 0 = most recent (prepend new entries)
  // Newest first, capped at MAX_EVENTS.
  eventLog: GameEventType[];
  // Keys outlive the capped event log: a once-per-run lesson must not repeat just because its
  // original row was the 101st one and fell off the visible history.
  reportedEventKeys: string[];
  eventLogReadThroughId: number;
  // The blackout underway as of the last tick, so the event log can say how bad it was once it's
  // over. Undefined while the lights are on. Kept in the slice so a save taken mid-blackout
  // reports the same length and shortfall as the run that was never interrupted.
  blackout?: { startMinute: number; unservedWh: number };
  // All-in $/MWh by fuel at the last monthly rollover, used to edge-detect cost-order changes.
  fuelCostSnapshot?: Partial<Record<FuelNameType, number>>;
  worldEvents: WorldEventStateType;
  // Headless balance harness only: baseline matrix cells run the identical strategy with authored
  // effects disabled. Undefined means enabled and is what every browser save/replay uses.
  storyEffectsDisabled?: boolean;
  // Headless balance harness only: run the identical strategy with the recurring regional wildfire
  // hazard switched off, to isolate its effect. Undefined means enabled (the browser default).
  wildfireHazardDisabled?: boolean;
  // Headless balance harness only: run the identical strategy with hail and extreme-cold hazards
  // switched off. Undefined means enabled (the browser default).
  weatherHazardsDisabled?: boolean;
  commissionedHydroSiteIds: string[];
  facilities: FacilityOperatingType[];
  // Absent in scenarios without intertie access. Enabled scenarios carry an explicit empty state.
  transmission?: TransmissionStateType;
  // Reducer-validated progress for the visible CEO objective. Replays rebuild it by applying the
  // same accepted state changes; saves persist and validate it so progress survives a reload.
  meaningfulDecisions: MeaningfulDecisionType[];
  // Headless balance harness can waive the decision gate to isolate economic outcomes.
  meaningfulDecisionGateWaived?: boolean;
  // Every simulation-affecting thing the player has done this run, for the replay attached to a
  // high score. Undefined means the run isn't being recorded: before a game starts, while one is
  // being watched, or once a run has grown past MAX_REPLAY_ACTIONS. Persisted with the rest of the
  // slice, so a replay survives a reload.
  replayLog?: ReplayActionType[];
  // Set only while watching a replay. Doubles as the "this is a replay" flag: player controls are
  // hidden, nothing is autosaved, and no score is submitted while it's here
  replayPlayback?: ReplayPlaybackType;
}

// Which units the player reads. Only ever affects display: everything the game stores and
// simulates is metric, and helpers/Units converts on the way out. See base/UnitsContext.
export type UnitSystemType = "metric" | "imperial";

// Which of the two palettes is being painted. See Theme.tsx (the charts, which draw to a canvas)
// and the custom properties at the top of app.scss (everything else)
export type ThemeModeType = "light" | "dark";

// What the player asked for, which is a third thing: "system" is a standing instruction to
// follow the OS rather than a palette of its own, and it can change while the game is open
export type ThemeChoiceType = ThemeModeType | "system";

export interface SettingsType {
  audioEnabled?: boolean;
  // Independent buses: zero mutes one without silencing the other. audioEnabled is the master
  // switch and remains undefined until the player grants first-run audio permission.
  musicVolume: number;
  soundEffectsVolume: number;
  units: UnitSystemType;
  theme: ThemeChoiceType;
}

export interface DialogType {
  message: string | React.JSX.Element | React.JSX.Element[];
  title: string;
  action?: (e: React.MouseEvent<HTMLElement>) => void;
  actionLabel?: string;
  // For dialogs where the second choice is an action of its own rather than "never mind" - the
  // end of a tutorial offers the next tutorial or the main menu, and neither is a dismissal.
  // Set alongside notCancellable, this replaces the close button rather than adding a third one
  secondaryAction?: (e: React.MouseEvent<HTMLElement>) => void;
  secondaryLabel?: string;
  tertiaryAction?: (e: React.MouseEvent<HTMLElement>) => void;
  tertiaryLabel?: string;
  destructive?: boolean;
  focusCancel?: boolean;
  notCancellable?: boolean;
  closeText?: string;
  // A milestone worth celebrating, where offering to install the app lands well
  offerInstall?: boolean;
  open: boolean;
}

export interface SnackbarType {
  action?: (e: React.MouseEvent<HTMLElement>) => void;
  actionLabel?: string;
  open: boolean;
  message: string;
  timeout: number;
}

/**
 * The end of a run, which the victory dialog turns into a score screen. Everything here is known
 * the moment the scenario ends; the rank and the score write are enrichment that lands later.
 */
export interface VictoryType {
  runIdentity?: RunIdentity;
  challenge?: ChallengeInvitation;
  scenarioId: number;
  scenarioName: string;
  difficulty: DifficultyType;
  score: number;
  // The per-category points behind `score`, in the order they should be listed
  breakdown: ScoreBreakdownType;
  endTitle?: string;
  endMessage?: string;
  // Whether the run counts for the leaderboard. A custom game (every one shares an id) or a
  // replay of someone else's run gets the breakdown without a rank or a personal best
  ranked: boolean;
  // The player's best on this scenario BEFORE this run, read at the moment the scenario ended so
  // that "was 640" reports the run before this one rather than the one just finished
  previousBest?: number;
  // A failed run still earns and submits a score, but the score screen must not celebrate it as a
  // completed mission or let the terminal game resume and submit the same run again
  outcome?: "completed" | "bankrupt" | "fired";
  debrief?: VictoryDebriefType;
}

export interface VictoryFleetCapacityType {
  fuel: FuelNameType;
  watts: number;
}

/** Browser-local save identity; deliberately independent of scenario and deterministic run IDs. */
export type SaveId = string;
export type SaveStatus = "inProgress" | "completed" | "bankrupt" | "fired";

export interface SaveGameType {
  savedAt: string;
  appVersion: string;
  game: GameType;
  commitmentForecast?: SavedCommitmentMetadata[];
}

/** Presentation data only: a saved result can never authorize another leaderboard submission. */
export type SavedRunResult = Pick<
  VictoryType,
  | "scenarioId"
  | "scenarioName"
  | "difficulty"
  | "score"
  | "breakdown"
  | "endTitle"
  | "endMessage"
  | "debrief"
> & { outcome: Exclude<SaveStatus, "inProgress"> };

export interface SaveMetadata {
  cloud?: {
    uid: string;
    id: string;
    version?: string;
    syncedRevision?: number;
    writerDeviceId?: string;
    uploadedAt?: number;
    uploadedMinute?: number;
  };
  id: SaveId;
  name: string;
  createdAt: string;
  lastPlayedAt?: string;
  savedAt: string;
  revision: number;
  status: SaveStatus;
  scenarioId: number;
  scenarioName: string;
  locationName: string;
  difficulty: DifficultyType;
  date: Pick<DateType, "month" | "year">;
}

export interface SaveFileType {
  name: string;
  status: SaveStatus;
  save: SaveGameType;
  result?: SavedRunResult;
}

export interface SaveRecord {
  metadata: SaveMetadata;
  save: SaveGameType;
  result?: SavedRunResult;
}

export interface SaveLease {
  saveId: SaveId;
  writerToken: string;
  revision: number;
}

/** A compact, serializable story of the run captured before the reducer's Immer draft expires. */
export interface VictoryDebriefType {
  demandWh?: number;
  startingFleet: VictoryFleetCapacityType[];
  finalFleet: VictoryFleetCapacityType[];
  startingCash: number;
  finalCash: number;
  finalCustomers: number;
  reliability: number;
  unservedWh: number;
  kgco2e: number;
  scenarioMetrics?: Array<{
    label: string;
    value: string;
    concept: ConceptNameType;
  }>;
  highlights: Array<
    Pick<GameEventType, "kind" | "label" | "message" | "importance">
  >;
}

export interface UIType {
  // A one-time guide requested by the data center launch; never part of a save or scenario.
  dataCenterGuideRequested?: boolean;
  challengeHref?: string;
  scenarioPreview?: number;
  previewDifficulty?: DifficultyType;
  // The Insights date range outlives the pane, which unmounts when a narrow layout switches cards.
  insightsViewport?: { viewport: [number, number]; month: number };
  evidenceRequest?: EvidenceRequestType;
  evidenceSequence?: number;
  evidenceRunId?: number;
  manualHelpEntry?: string;
  dialog: DialogType;
  snackbar: SnackbarType;
  // True only while the player is physically reordering the fleet. Expensive sibling panes can
  // defer their next projection until the drop, when the temporarily suspended clock resumes.
  facilityDragActive: boolean;
  // The facility the player has clicked in the fleet list, or null for none. UI rather than game
  // state: it changes nothing about the simulation, and it is read by all three panes -- the
  // fleet row expands, Supply by Fuel dims everything it doesn't burn, and Insights reports what
  // it has earned. Cleared when the run ends, or when the facility is sold out from under it
  selectedFacilityId: number | null;
  arrivingFacilityId?: number;
  // The score screen for a run that just ended, or null when none has. Its own slot rather than a
  // `dialog`, because the shared dialog only holds a title and a message and this one fills
  // itself in as async results arrive
  victory: VictoryType | null;
}

// A player's best run on one scenario, mirrored from users/{uid}.bests so that the victory dialog
// can compare against it without a read
export interface BestScoreType {
  score: number;
  difficulty: string;
  // Epoch ms rather than a Timestamp: this one is read back into a plain Redux slice
  date: number;
}

export interface UserType {
  profileRequestId?: string;
  nameRequestId?: string;
  logoutRequestId?: string;
  uid?: string;
  // The leaderboard name, once one has been claimed
  displayName?: string;
  // The name the identity provider knows them by. Kept only to seed the name dialog -- it is
  // never what the board shows, since it is neither unique nor within the board's charset
  googleDisplayName?: string;
  // Keyed by scenario id as a string, which is how it comes back out of Firestore
  bests?: { [scenarioId: string]: BestScoreType };
  // Set once users/{uid} has been read or created, so the UI can tell "this player has no name"
  // apart from "we have not looked yet"
  profileLoaded?: boolean;
  // Whether to prompt for a display name. Set on first login, and by the Settings card's
  // "Change name"; cleared once the dialog is answered or dismissed
  needsDisplayName?: boolean;
}

export type TransitionClassType = "next" | "prev" | "instant" | "nav";

export interface AppStateType {
  // Optional for injected/headless state fixtures; the browser store always includes it.
  saves?: import("./SaveLibrary").SaveLibraryState;
  card: CardType;
  game: GameType;
  settings: SettingsType;
  ui: UIType;
  user: UserType;
}

/** Authored time-triggered choices; IDs are persisted in story occurrences. */
export interface ScenarioChoiceType {
  id: string;
  scenarioId: number;
  atMonth: number;
  title: string;
  message: string;
  options: {
    id: string;
    label: string;
    message: string;
    cost: (difficulty: DifficultyType) => number;
    description?: string;
    /** One-time company contribution, never plant electricity sales. */
    upfrontGrant?: (difficulty: DifficultyType) => number;
    loadAdditions?: ScenarioLoadAdditionType[];
    /** False for a response that preserves the baseline without changing the operating plan. */
    meaningful?: boolean;
  }[];
}

/** Canonical initial inputs, captured before the first simulation tick. */
export interface RunIdentity {
  scenarioId: number;
  seed: number;
  difficulty: DifficultyType;
  compatibilityId: string;
  origin: "authored" | "custom" | "tutorial" | "replay";
  inputs: {
    scenario: string;
    location: LocationType;
    facilities: ScenarioFacilityType[];
    cash: number;
    customers: number;
    meaningfulDecisionGateWaived: boolean;
  };
}
export interface AuthoredRunReference {
  scenarioId: number;
  seed: number;
  difficulty: DifficultyType;
  compatibilityId: string;
}
export interface ChallengeInvitation {
  run: AuthoredRunReference;
  target: number;
}
